import * as Api from '@ViewModels';
import { ActivationStatus } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useUserQuery } from '../../../../queries';
import { SuccessCheckIcon } from '../../../../web/components/svgs/icons/SuccessCheckIcon';
import { styleSheet } from './styles';

const getStatusDescription = (status: ActivationStatus): 'Connected' | 'Not Connected' | 'Invited' | 'Deactivated' => {
	switch (status) {
		case Api.ActivationStatus.ACTIVE: {
			return 'Connected';
		}
		case Api.ActivationStatus.CANCELED:
		case Api.ActivationStatus.DEACTIVATED: {
			return 'Deactivated';
		}
		case Api.ActivationStatus.INIT: {
			return 'Invited';
		}
		default: {
			throw new Error(`Unknown status: ${status}`);
		}
	}
};

interface IProps {
	integrationUser: Api.IIntegrationUserMapping;
	connectedLevIds: string[];
	showConnectionStatusColumns?: boolean;
	children?: React.ReactNode;
	accountId: string;
}

function _IntegrationsManageUsersTablerow({
	integrationUser,
	accountId,
	connectedLevIds,
	showConnectionStatusColumns = true,
	children,
}: IProps) {
	const userQuery = useUserQuery({
		enabled: Boolean(integrationUser.levitateUser?.id),
		id: integrationUser.levitateUser?.id,
		impersonationContext: {
			account: { id: accountId },
		},
		meta: {
			errorLogging: {
				category: 'IntegrationsManageUsersTableRow',
				action: 'UserQuery-Error',
			},
		},
	});

	const isConnected =
		integrationUser.levitateUser?.id && connectedLevIds?.indexOf(integrationUser.levitateUser.id) !== -1;
	const statusDescription = userQuery.data?.activationStatus
		? getStatusDescription(userQuery.data.activationStatus)
		: '';

	return (
		<tr className={css(styleSheet.container)}>
			<td className={css(styleSheet.cell, styleSheet.initialsColumnCell)}>{integrationUser.integrationId}</td>
			{children}
			{showConnectionStatusColumns ? (
				<>
					<td className={css(styleSheet.cell, styleSheet.statusIndicatorColumnCell)}>
						{(userQuery.data?.activationStatus === ActivationStatus.ACTIVE ||
							userQuery.data?.activationStatus === ActivationStatus.INIT) &&
						isConnected ? (
							<SuccessCheckIcon
								className={css(
									userQuery.data?.activationStatus !== ActivationStatus.ACTIVE
										? styleSheet.statusIndicatorInactive
										: null
								)}
							/>
						) : null}
					</td>
					<td className={css(styleSheet.cell, styleSheet.statusColumnCell)}>
						<div
							className={css(
								styleSheet.status,
								userQuery.data?.activationStatus === ActivationStatus.ACTIVE && isConnected
									? styleSheet.statusConnect
									: null
							)}
						>
							{userQuery.data?.activationStatus === ActivationStatus.ACTIVE && !isConnected
								? 'Not Connected'
								: statusDescription}
						</div>
					</td>
				</>
			) : null}
		</tr>
	);
}

export const IntegrationsManageUsersTableRow = observer(_IntegrationsManageUsersTablerow);
